import { AnimatePresence, cubicBezier, motion, useReducedMotion } from 'framer-motion';
import { BookOpen, ChevronLeft, ChevronRight, Film, Headphones, Music2, Play } from 'lucide-react';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useShop } from '../../../shared/store';
import type { Product } from '../../../shared/types';
import { filterProductsByMediaType } from '../../../shared/utils/productMedia';
import { BACKEND_BASE_URL } from '../../../shared/config/api';

type HeroSlideId = 'books' | 'audio' | 'video';

type HeroSlide = {
  id: HeroSlideId;
  eyebrow: string;
  title: {
    first: string;
    second: string;
    highlight: string;
  };
  description: string;
  primaryCta: { to: string; label: string };
  secondaryCta: { to: string; label: string };
  accentClass: string;
  stats: Array<{ value: string; label: string }>;
};

const heroEase = cubicBezier(0.22, 1, 0.36, 1);

const slides: HeroSlide[] = [
  {
    id: 'books',
    eyebrow: 'Digital Library',
    title: { first: 'Explore', second: 'Thousands of', highlight: 'Books & Guides' },
    description:
      'Dive into curated eBooks, textbooks, and guides across every genre. Read at your own pace, anywhere in the world.',
    primaryCta: { to: '/books', label: 'Browse Books' },
    secondaryCta: { to: '/about', label: 'Learn More' },
    accentClass: 'text-orange-400',
    stats: [
      { value: '50K+', label: 'Books' },
      { value: '120+', label: 'Publishers' },
      { value: '4.9*', label: 'Reviews' },
    ],
  },
  {
    id: 'audio',
    eyebrow: 'Audio Library',
    title: { first: 'Listen to', second: 'Stories', highlight: 'Come Alive' },
    description:
      'Immerse yourself in premium audiobooks, podcasts, and exclusive audio content narrated by world-class voices.',
    primaryCta: { to: '/audio', label: 'Start Listening' },
    secondaryCta: { to: '/about', label: 'Learn More' },
    accentClass: 'text-iw-teal',
    stats: [
      { value: '30K+', label: 'Audiobooks' },
      { value: '500+', label: 'Podcasts' },
      { value: 'HD', label: 'Quality' },
    ],
  },
  {
    id: 'video',
    eyebrow: 'Video Hub',
    title: { first: 'Watch,', second: 'Learn &', highlight: 'Be Inspired' },
    description:
      'Stream educational videos, documentaries, masterclasses, and exclusive Haapa originals in stunning quality.',
    primaryCta: { to: '/animation', label: 'Watch Now' },
    secondaryCta: { to: '/about', label: 'Learn More' },
    accentClass: 'text-amber-300',
    stats: [
      { value: '10K+', label: 'Videos' },
      { value: '4K', label: 'Quality' },
      { value: '200+', label: 'Courses' },
    ],
  },
];

const compactIntegerFormatter = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const courseKeywords = ['course', 'courses', 'masterclass', 'masterclasses', 'lesson', 'lessons', 'training', 'tutorial', 'tutorials'];

const getPublisherName = (product: Product): string =>
  product.publishedCompany?.trim() || product.uploaderName?.trim() || product.authorName?.trim() || product.author?.trim() || '';

const formatMetricValue = (value: number): string => compactIntegerFormatter.format(Math.max(0, value));

const isCourseLikeProduct = (product: Product): boolean => {
  const searchableText = [
    product.nameKey,
    product.descriptionKey,
    product.summary,
    product.categoryKey,
    ...(product.categories?.flatMap((category) => [category.slug, category.label]) ?? []),
  ]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(' ')
    .toLowerCase();

  return courseKeywords.some((keyword) => searchableText.includes(keyword));
};

const AUTOPLAY_DELAY_MS = 3000;

const moduloIndex = (index: number, length: number) => ((index % length) + length) % length;

const slideIcons: Record<HeroSlideId, typeof BookOpen> = {
  books: BookOpen,
  audio: Headphones,
  video: Film,
};

const noteSpecs = [
  { left: '8%', top: '18%', size: 18, opacity: 0.22, delay: '0s', duration: '7.4s', rotate: -8 },
  { left: '16%', top: '62%', size: 22, opacity: 0.18, delay: '1.2s', duration: '8.2s', rotate: 10 },
  { left: '36%', top: '10%', size: 16, opacity: 0.14, delay: '0.6s', duration: '7.8s', rotate: 6 },
  { left: '58%', top: '26%', size: 26, opacity: 0.16, delay: '1.8s', duration: '9.0s', rotate: -12 },
  { left: '64%', top: '74%', size: 18, opacity: 0.12, delay: '0.9s', duration: '8.6s', rotate: 14 },
  { left: '78%', top: '14%', size: 20, opacity: 0.12, delay: '2.2s', duration: '9.4s', rotate: 8 },
  { left: '86%', top: '54%', size: 28, opacity: 0.1, delay: '1.5s', duration: '10.2s', rotate: -5 },
  { left: '92%', top: '28%', size: 16, opacity: 0.09, delay: '2.8s', duration: '8.8s', rotate: 16 },
];

const buildNoteStyle = (spec: (typeof noteSpecs)[number], baseOpacity: number): CSSProperties =>
  ({
    left: spec.left,
    top: spec.top,
    opacity: spec.opacity * baseOpacity,
    ['--haapa-note-delay' as never]: spec.delay,
    ['--haapa-note-duration' as never]: spec.duration,
    ['--haapa-note-rotate' as never]: `${spec.rotate}deg`,
  }) satisfies CSSProperties;

type FeaturedVideo = {
  src: string;
  poster?: string;
  label?: string;
};

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:');

const resolveAssetUrl = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  if (isAbsoluteUrl(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    // Keep same-origin assets (Vite/public) working, but also support common backend-served media paths.
    const backendPrefixes = ['/uploads', '/media', '/videos', '/files', '/storage'];
    if (backendPrefixes.some((prefix) => trimmed.startsWith(prefix))) {
      return `${BACKEND_BASE_URL}${trimmed}`;
    }

    return trimmed;
  }

  return `${BACKEND_BASE_URL}/${trimmed}`;
};

const VideoPreviewFrame = ({ video }: { video?: FeaturedVideo }) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);
  const resolvedSrc = useMemo(() => resolveAssetUrl(video?.src), [video?.src]);
  const resolvedPoster = useMemo(() => resolveAssetUrl(video?.poster), [video?.poster]);

  if (!resolvedSrc || hasError) {
    return (
      <div className="relative aspect-video w-full bg-gradient-to-br from-slate-950/60 via-slate-950/35 to-slate-900/10">
        <div className="pointer-events-none absolute inset-0 opacity-25 [background-image:radial-gradient(rgba(148,163,184,0.55)_1px,transparent_1px)] [background-size:16px_16px]" />
        <div className="relative flex h-full items-center justify-center">
          <div className="grid h-16 w-16 place-items-center rounded-full border border-white/20 bg-white/10 text-white">
            <Play className="h-7 w-7 fill-current text-white" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <video
        key={resolvedSrc}
        src={resolvedSrc}
        poster={resolvedPoster}
        className="aspect-video w-full bg-black object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        onError={() => setHasError(true)}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
      <div className="pointer-events-none absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
        <span className="h-2 w-2 rounded-full bg-amber-300" />
        {t('homeHero.visual.freeAnimation', { defaultValue: 'Free animation' })}
      </div>
      {video?.label ? (
        <div className="pointer-events-none absolute bottom-4 left-4 max-w-[80%] rounded-2xl bg-slate-950/55 px-4 py-3 text-white shadow-lg backdrop-blur">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70">
            {t('homeHero.visual.nowPlaying', { defaultValue: 'Now playing' })}
          </p>
          <p className="mt-1 line-clamp-1 text-sm font-semibold">{video.label}</p>
        </div>
      ) : null}
    </>
  );
};

const GooglePlayBadgeIcon = () => (
  <svg viewBox="0 0 48 48" className="h-11 w-11" aria-hidden="true" focusable="false">
    <path d="M8 7.5L26.5 26 8 44.5z" fill="#00d2ff" />
    <path d="M8 7.5l23 13.5-4.5 5L8 7.5z" fill="#00f076" />
    <path d="M8 44.5l18.5-18.5 4.5 5L8 44.5z" fill="#ff5252" />
    <path d="M31 21L38.8 25.6c1.6.95 1.6 3.25 0 4.2L31 34.5 22 25.5z" fill="#ffcc00" />
  </svg>
);

const AppleStoreBadgeIcon = () => (
  <svg viewBox="0 0 16 16" className="h-6 w-6 fill-current text-slate-950" aria-hidden="true" focusable="false">
    <path d="M11.182 0c.051.79-.216 1.386-.606 1.84-.396.466-1.044.826-1.684.776-.08-.729.242-1.399.576-1.79C9.862.35 10.531.038 11.18 0h.003zM13.56 12.153c-.298.687-.44.994-.824 1.595-.536.857-1.294 1.925-2.234 1.933-.836.008-1.05-.551-2.184-.542-1.135.005-1.37.553-2.207.545-.94-.008-1.656-.97-2.192-1.827C2.417 11.463 2.262 8.653 3.186 7.221c.655-1.026 1.694-1.625 2.67-1.625.995 0 1.622.56 2.446.56.801 0 1.29-.56 2.442-.56.87 0 1.79.48 2.443 1.31-2.138 1.19-1.792 4.275.373 5.247z" />
  </svg>
);

const HeroVisual = ({ slideId, featuredVideo }: { slideId: HeroSlideId; featuredVideo?: FeaturedVideo }) => {
  const { t } = useTranslation();
  if (slideId === 'books') {
    return (
      <div className="relative mx-auto w-full max-w-[520px] sm:max-w-[600px] lg:mx-0 lg:ml-auto lg:max-w-[720px]">
        <div className="pointer-events-none absolute -left-12 -top-10 h-44 w-44 rounded-full bg-orange-500/20 blur-3xl haapa-glow-pulse" />
        <div className="pointer-events-none absolute -bottom-12 -right-12 h-52 w-52 rounded-full bg-iw-teal/15 blur-3xl haapa-glow-pulse [animation-delay:1.2s]" />

        <div className="relative overflow-hidden rounded-[28px] border border-white/60 bg-white/20 p-6 shadow-[0_26px_70px_-50px_rgba(15,23,42,0.22)] backdrop-blur-xl sm:p-7 lg:p-8 haapa-hero-drift">
          <div className="absolute inset-0 bg-gradient-to-br from-white/55 via-white/20 to-transparent" />

          <div className="relative grid gap-6 sm:grid-cols-[1fr_auto] sm:items-center">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                {t('homeHero.visual.trendingNow', { defaultValue: 'Trending Now' })}
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {t('homeHero.visual.readingToday', { defaultValue: '1,240 reading today' })}
              </p>
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                <span className="h-2 w-2 rounded-full bg-orange-400" />
                {t('homeHero.visual.newArrivalsWeekly', { defaultValue: 'New arrivals weekly' })}
              </div>
            </div>

            <div className="relative">
              <img
                src="/ChatGPT Image Mar 12, 2026, 12_39_27 PM.png"
                alt={t('homeHero.visual.africanBooksAlt', { defaultValue: 'African books' })}
                className="h-72 w-auto select-none object-contain drop-shadow-[0_28px_50px_rgba(15,23,42,0.25)] sm:h-80 lg:h-[360px]"
                loading="lazy"
              />
              <div className="pointer-events-none absolute -left-4 bottom-8 rounded-2xl bg-slate-950/70 px-4 py-3 text-white shadow-lg backdrop-blur haapa-float-card">
                <p className="text-xs font-semibold">{t('homeHero.visual.newArrival', { defaultValue: 'New Arrival' })}</p>
                <p className="text-[11px] text-white/75">
                  {t('homeHero.visual.booksThisWeek', { defaultValue: '24 books this week' })}
                </p>
              </div>
              <div className="pointer-events-none absolute -right-6 top-6 rounded-2xl bg-slate-950/70 px-4 py-3 text-white shadow-lg backdrop-blur haapa-float-card [animation-delay:1.1s]">
                <p className="text-xs font-semibold">{t('homeHero.visual.trending', { defaultValue: 'Trending' })}</p>
                <p className="text-[11px] text-white/75">
                  {t('homeHero.visual.topPicksToday', { defaultValue: 'Top picks today' })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (slideId === 'audio') {
    return (
      <div className="relative mx-auto w-full max-w-[560px] sm:max-w-[620px] lg:mx-0 lg:ml-auto lg:max-w-[740px]">
        <div className="pointer-events-none absolute -left-10 -top-10 h-44 w-44 rounded-full bg-iw-teal/20 blur-3xl haapa-glow-pulse" />
        <div className="pointer-events-none absolute -bottom-14 -right-12 h-56 w-56 rounded-full bg-orange-500/10 blur-3xl haapa-glow-pulse [animation-delay:1.6s]" />

        <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-55px_rgba(15,23,42,0.35)] sm:p-7 lg:p-8 haapa-hero-drift">
          <div className="absolute inset-0 opacity-70 [background-image:radial-gradient(circle_at_20%_20%,rgba(83,185,193,0.20),transparent_55%),radial-gradient(circle_at_80%_10%,rgba(249,115,22,0.12),transparent_55%)]" />
          <div className="relative space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-100 text-slate-900">
                  <Music2 className="h-6 w-6 text-iw-teal" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {t('homeHero.audio.sampleTitle', { defaultValue: 'The Art of Thinking' })}
                  </p>
                  <p className="text-xs text-slate-600">
                    {t('homeHero.audio.sampleSubtitle', { defaultValue: 'Haapa Audio - Chapter 4' })}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                aria-label={t('homeHero.audio.addFavoriteAria', { defaultValue: 'Add to favorites' })}
              >
                ♥
              </button>
            </div>

            <div className="haapa-wave flex items-end justify-between gap-1 rounded-2xl bg-slate-100 px-4 py-4">
              {Array.from({ length: 22 }).map((_, idx) => (
                <span key={idx} className="h-7 w-2 rounded-full bg-iw-teal/70 sm:h-8" />
              ))}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500">
                <span>14:22</span>
                <span>38:00</span>
              </div>
              <div className="relative h-2 rounded-full bg-slate-200">
                <div className="absolute left-0 top-0 h-full w-[46%] rounded-full bg-iw-teal/80" />
                <div className="absolute left-[46%] top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white shadow-[0_10px_22px_rgba(0,0,0,0.45)]" />
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <button
                type="button"
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                ⟲ 15s
              </button>
              <button
                type="button"
                className="grid h-14 w-14 place-items-center rounded-2xl bg-iw-teal text-slate-950 shadow-[0_18px_40px_-20px_rgba(83,185,193,0.85)]"
                aria-label="Play preview"
              >
                <Play className="h-6 w-6 fill-current" />
              </button>
              <button
                type="button"
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                30s ⟳
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto w-full max-w-[560px] sm:max-w-[620px] lg:mx-0 lg:ml-auto lg:max-w-[740px]">
      <div className="pointer-events-none absolute -left-10 -top-10 h-44 w-44 rounded-full bg-amber-300/18 blur-3xl haapa-glow-pulse" />
      <div className="pointer-events-none absolute -bottom-14 -right-12 h-56 w-56 rounded-full bg-orange-500/10 blur-3xl haapa-glow-pulse [animation-delay:1.4s]" />

      <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_26px_70px_-50px_rgba(15,23,42,0.35)] sm:p-7 lg:p-8 haapa-hero-drift">
        <div className="absolute inset-0 opacity-70 [background-image:radial-gradient(circle_at_20%_20%,rgba(251,191,36,0.16),transparent_55%),radial-gradient(circle_at_85%_20%,rgba(249,115,22,0.12),transparent_55%)]" />

      <div className="relative space-y-5">
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-black shadow-[0_18px_55px_-40px_rgba(15,23,42,0.55)]">
            <VideoPreviewFrame video={featuredVideo} />
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            {[
              { label: 'Episodes', className: 'from-orange-500/45 to-amber-300/35' },
              { label: 'Topics', className: 'from-iw-teal/30 to-sky-blue/25' },
              { label: 'Quizzes', className: 'from-emerald-500/30 to-iw-teal/25' },
              { label: 'Notes', className: 'from-violet-500/30 to-orange-500/20' },
            ].map((tile) => (
              <div
                key={tile.label}
                className={`rounded-2xl border border-white/10 bg-gradient-to-br ${tile.className} p-3 text-center text-xs font-semibold text-white/85 shadow-[0_16px_40px_-30px_rgba(2,6,23,0.9)]`}
              >
                {tile.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const HomeHeroCarousel = () => {
  const shouldReduceMotion = useReducedMotion();
  const { t } = useTranslation();
  const { products } = useShop();
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [isPaused, setIsPaused] = useState(false);

  const heroMetrics = useMemo(() => {
    const booksCount = filterProductsByMediaType(products, 'book').length;
    const audiobooksCount = filterProductsByMediaType(products, 'audio').length;
    const videosCount = filterProductsByMediaType(products, 'animation').length;
    const publishers = new Set<string>();

    let totalReviews = 0;
    let coursesCount = 0;

    products.forEach((product) => {
      const publisherName = getPublisherName(product);
      if (publisherName) {
        publishers.add(publisherName.toLowerCase());
      }

      totalReviews += Math.max(0, Math.floor(Number(product.reviewCount) || 0));

      if (isCourseLikeProduct(product)) {
        coursesCount += 1;
      }
    });

    return {
      booksCount,
      audiobooksCount,
      videosCount,
      publishersCount: publishers.size,
      reviewsCount: totalReviews,
      coursesCount,
    };
  }, [products]);

  const resolvedSlides = useMemo<HeroSlide[]>(
    () =>
      slides.map((slide) => {
        const baseKey = `homeHero.slides.${slide.id}`;
        const resolvedStats =
          slide.id === 'books'
            ? [
                { value: formatMetricValue(heroMetrics.booksCount), label: 'Books' },
                { value: formatMetricValue(heroMetrics.publishersCount), label: 'Publishers' },
                { value: formatMetricValue(heroMetrics.reviewsCount), label: 'Reviews' },
              ]
            : slide.id === 'audio'
              ? [
                  { value: formatMetricValue(heroMetrics.audiobooksCount), label: 'Audiobooks' },
                  { value: formatMetricValue(heroMetrics.publishersCount), label: 'Publishers' },
                  { value: formatMetricValue(heroMetrics.reviewsCount), label: 'Reviews' },
                ]
              : [
                  { value: formatMetricValue(heroMetrics.videosCount), label: 'Videos' },
                  { value: formatMetricValue(heroMetrics.coursesCount), label: 'Courses' },
                  { value: formatMetricValue(heroMetrics.reviewsCount), label: 'Reviews' },
                ];

        return {
          ...slide,
          eyebrow: t(`${baseKey}.eyebrow`, { defaultValue: slide.eyebrow }),
          title: {
            first: t(`${baseKey}.title.first`, { defaultValue: slide.title.first }),
            second: t(`${baseKey}.title.second`, { defaultValue: slide.title.second }),
            highlight: t(`${baseKey}.title.highlight`, { defaultValue: slide.title.highlight }),
          },
          description: t(`${baseKey}.description`, { defaultValue: slide.description }),
          primaryCta: {
            ...slide.primaryCta,
            label: t(`${baseKey}.primaryCta`, { defaultValue: slide.primaryCta.label }),
          },
          secondaryCta: {
            ...slide.secondaryCta,
            label: t(`${baseKey}.secondaryCta`, { defaultValue: slide.secondaryCta.label }),
          },
          stats: resolvedStats.map((stat, idx) => ({
            ...stat,
            label: t(`${baseKey}.stats.${idx}.label`, { defaultValue: stat.label }),
          })),
        };
      }),
    [heroMetrics, t],
  );

  const activeSlide = resolvedSlides[activeIndex] ?? resolvedSlides[0];
  const ActiveIcon = slideIcons[activeSlide.id];
  const appDownloadButtons = [
    {
      id: 'android',
      to: '/apps/android',
      eyebrow: t('homeHero.download.androidEyebrow', { defaultValue: 'GET IT ON' }),
      label: t('homeHero.download.androidLabel', { defaultValue: 'Google Play' }),
    },
    {
      id: 'ios',
      to: '/apps/ios',
      eyebrow: t('homeHero.download.iosEyebrow', { defaultValue: 'Download on the' }),
      label: t('homeHero.download.iosLabel', { defaultValue: 'App Store' }),
    },
  ] as const;

  const noteOpacity = activeSlide.id === 'audio' ? 1 : 0.45;
  const noteColorClass =
    activeSlide.id === 'audio' ? 'text-iw-teal' : activeSlide.id === 'video' ? 'text-amber-400' : 'text-orange-400';

  const featuredVideo = useMemo<FeaturedVideo | undefined>(() => {
    const animationProducts = filterProductsByMediaType(products, 'animation');
    const freeAnimation = animationProducts.find((product) => Number(product.price) === 0 && Boolean(product.contentUrl || product.videoUrl));
    const fallback = animationProducts.find((product) => Boolean(product.contentUrl || product.videoUrl));
    const selected = freeAnimation ?? fallback;
    const src = selected?.contentUrl || selected?.videoUrl || '/91df70d7-8fde-4cdd-8987-d2c1c6cf7f75.mp4';

    return {
      src,
      poster: selected?.imageUrl || '/wallpaper for homepage 2.png',
      label: selected
        ? t(selected.nameKey)
        : t('homeHero.animationPreview', { defaultValue: 'Haapa Animation Preview' }),
    };
  }, [products, t]);

  const goTo = (nextIndex: number) => {
    const normalized = moduloIndex(nextIndex, resolvedSlides.length);
    setDirection(1);
    setActiveIndex(normalized);
  };

  const goNext = () => goTo(activeIndex + 1);
  const goPrev = () => goTo(activeIndex - 1);

  useEffect(() => {
    if (shouldReduceMotion || isPaused || resolvedSlides.length < 2) {
      return;
    }

    const autoplay = window.setInterval(() => {
      goNext();
    }, AUTOPLAY_DELAY_MS);

    return () => window.clearInterval(autoplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReduceMotion, isPaused, activeIndex]);

  const slideTransition = useMemo(() => {
    if (shouldReduceMotion) {
      return {
        enter: () => ({ opacity: 0 }),
        center: { opacity: 1, transition: { duration: 0.01 } },
        exit: () => ({ opacity: 0, transition: { duration: 0.01 } }),
      };
    }

    return {
      enter: (customDirection: number) => ({
        opacity: 0,
        x: customDirection > 0 ? 36 : -36,
        scale: 0.98,
      }),
      center: {
        opacity: 1,
        x: 0,
        scale: 1,
        transition: { duration: 0.62, ease: heroEase },
      },
      exit: (customDirection: number) => ({
        opacity: 0,
        x: customDirection > 0 ? -22 : 22,
        scale: 0.985,
        transition: { duration: 0.32, ease: heroEase },
      }),
    };
  }, [shouldReduceMotion]);

  const textContainer = useMemo(
    () => ({
      hidden: { opacity: 1 },
      show: {
        opacity: 1,
        transition: shouldReduceMotion ? undefined : { staggerChildren: 0.085, delayChildren: 0.08 },
      },
    }),
    [shouldReduceMotion],
  );

  const textItem = useMemo(
    () => ({
      hidden: shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 },
      show: shouldReduceMotion
        ? { opacity: 1, y: 0 }
        : { opacity: 1, y: 0, transition: { duration: 0.5, ease: heroEase } },
    }),
    [shouldReduceMotion],
  );

  return (
    <section
      className="relative isolate overflow-hidden bg-white pb-16 pt-24 sm:pb-20 sm:pt-28"
      aria-roledescription="carousel"
      aria-label={t('homeHero.ariaLabel', { defaultValue: 'Haapa hero highlights' })}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goPrev();
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goNext();
        }
      }}
    >
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 [background-image:radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.20),transparent_52%),radial-gradient(circle_at_85%_10%,rgba(83,185,193,0.18),transparent_52%),radial-gradient(circle_at_55%_90%,rgba(251,146,60,0.12),transparent_60%)]"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 opacity-[0.45] [background-image:radial-gradient(rgba(15,23,42,0.12)_1px,transparent_1px)] [background-size:18px_18px]"
          aria-hidden="true"
        />

        <div className="pointer-events-none absolute -left-32 top-24 h-[520px] w-[520px] rounded-full bg-orange-400/20 blur-3xl haapa-glow-pulse" />
        <div className="pointer-events-none absolute -right-40 top-10 h-[560px] w-[560px] rounded-full bg-iw-teal/18 blur-3xl haapa-glow-pulse [animation-delay:1.4s]" />

        {noteSpecs.map((spec, idx) => (
          <span
            key={`${spec.left}-${spec.top}-${idx}`}
            className={`pointer-events-none absolute haapa-hero-note ${noteColorClass}`}
            style={buildNoteStyle(spec, noteOpacity)}
            aria-hidden="true"
          >
            <Music2 className="h-5 w-5" style={{ width: spec.size, height: spec.size }} />
          </span>
        ))}
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-14">
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-0 top-1/2 z-10 hidden -translate-x-10 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-3 text-slate-800 shadow-lg backdrop-blur transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70 lg:grid"
            aria-label={t('homeHero.prev', { defaultValue: 'Previous slide' })}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            type="button"
            onClick={goNext}
            className="absolute right-0 top-1/2 z-10 hidden translate-x-10 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-3 text-slate-800 shadow-lg backdrop-blur transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70 lg:grid"
            aria-label={t('homeHero.next', { defaultValue: 'Next slide' })}
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.article
              key={activeSlide.id}
              custom={direction}
              variants={slideTransition}
              initial="enter"
              animate="center"
              exit="exit"
              className="space-y-8"
            >
              <motion.div
                variants={textContainer}
                initial="hidden"
                animate="show"
                className="space-y-4 text-slate-900"
              >
                <motion.p
                  variants={textItem}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/85 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700 shadow-sm backdrop-blur"
                >
                  <ActiveIcon className="h-4 w-4 text-orange-500" />
                  {activeSlide.eyebrow}
                </motion.p>

                <motion.h1
                  variants={textItem}
                  className="max-w-2xl text-5xl font-semibold leading-[1.03] md:text-6xl lg:text-7xl"
                >
                  <span className="block">{activeSlide.title.first}</span>
                  <span className="block">{activeSlide.title.second}</span>
                  <span className={`block ${activeSlide.accentClass}`}>{activeSlide.title.highlight}</span>
                </motion.h1>

                <motion.p variants={textItem} className="max-w-xl text-base text-slate-600 md:text-lg">
                  {activeSlide.description}
                </motion.p>

                <motion.div variants={textItem} className="flex flex-wrap items-center gap-3 pt-2">
                  <Link
                    to={activeSlide.primaryCta.to}
                    className="rounded-2xl bg-orange-500 px-7 py-4 text-sm font-semibold text-white shadow-[0_22px_60px_-35px_rgba(249,115,22,0.95)] transition hover:bg-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                  >
                    {activeSlide.primaryCta.label}
                  </Link>
                  <Link
                    to={activeSlide.secondaryCta.to}
                    className="rounded-2xl border border-slate-200 bg-white px-7 py-4 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                  >
                    {activeSlide.secondaryCta.label} {'->'}
                  </Link>
                </motion.div>

                <motion.div variants={textItem} className="grid gap-3 pt-1 sm:max-w-[540px] sm:grid-cols-2">
                  {appDownloadButtons.map((button) => (
                    <Link
                      key={button.id}
                      to={button.to}
                      aria-label={button.label}
                      className={`flex items-center border border-slate-200 bg-white text-left shadow-[0_18px_45px_-35px_rgba(15,23,42,0.55)] transition hover:-translate-y-0.5 hover:shadow-[0_26px_55px_-35px_rgba(15,23,42,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 ${
                        button.id === 'ios' ? 'gap-3 rounded-[26px] px-4 py-3.5' : 'gap-4 rounded-[30px] px-5 py-4'
                      }`}
                    >
                      <span
                        className={`flex shrink-0 items-center justify-center ${
                          button.id === 'ios'
                            ? 'h-10 w-10 rounded-2xl bg-slate-100 shadow-inner shadow-slate-200/70'
                            : 'h-12 w-12'
                        }`}
                      >
                        {button.id === 'android' ? <GooglePlayBadgeIcon /> : <AppleStoreBadgeIcon />}
                      </span>
                      <span className="min-w-0">
                        <span
                          className={`block font-semibold uppercase text-slate-500 ${
                            button.id === 'ios' ? 'text-[10px] tracking-[0.16em]' : 'text-[11px] tracking-[0.18em]'
                          }`}
                        >
                          {button.eyebrow}
                        </span>
                        <span
                          className={`block font-semibold leading-tight text-slate-950 ${
                            button.id === 'ios' ? 'text-lg' : 'text-xl'
                          }`}
                        >
                          {button.label}
                        </span>
                      </span>
                    </Link>
                  ))}
                </motion.div>
              </motion.div>

              <div className="flex flex-wrap items-center gap-6 pt-2 text-slate-700">
                {activeSlide.stats.map((stat) => (
                  <div key={stat.label} className="min-w-[90px]">
                    <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.article>
          </AnimatePresence>

          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={`${activeSlide.id}-visual`}
              custom={direction}
              variants={slideTransition}
              initial="enter"
              animate="center"
              exit="exit"
              className="lg:justify-self-stretch"
            >
              <HeroVisual slideId={activeSlide.id} featuredVideo={featuredVideo} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-2">
          {resolvedSlides.map((slide, idx) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goTo(idx)}
              className={`h-2.5 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 ${
                idx === activeIndex ? 'w-10 bg-orange-500' : 'w-2.5 bg-slate-300 hover:bg-slate-400'
              }`}
              aria-label={t('homeHero.gotoSlide', { defaultValue: 'Go to {{label}} slide', label: slide.eyebrow })}
              aria-current={idx === activeIndex ? 'true' : undefined}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeHeroCarousel;
